import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Purpose: React context provider wrapping {@link UserManager} and the
 *          companion {@link useAuth} hook. Exposes `isAuthenticated`,
 *          `user`, `login`, `logout` and a `getAccessToken` accessor to
 *          children.
 *
 * Implementation notes:
 *   - On mount the provider completes any in-flight redirect callback
 *     (presence of `code` + `state` in the URL) and then loads the
 *     persisted user from the configured store. Once that resolves
 *     `isLoading` flips to false.
 *   - Subscribes to `userLoaded` / `userUnloaded` events so silent renews
 *     and explicit logouts propagate to the React tree without a manual
 *     refetch.
 *   - The provider is intentionally NOT a route component — wrap your
 *     entire app once near the React root. RequireAuth (separate file)
 *     handles per-route gating.
 *
 * Created: 2026-04-30 11:00
 *
 * Changelog:
 *   - 2026-04-30 11:00 — Initial version.
 *   - 2026-05-06 11:30 — Added `useRoles` / `useHasRole` hooks that read
 *                        Zitadel's `urn:zitadel:iam:org:project:roles`
 *                        claim from the active user's profile so SPAs
 *                        can gate UI on the same role keys the API uses.
 *   - 2026-05-06 12:00 — `login()` now accepts an optional `returnTo`
 *                        path that the shared @ada/auth-shell reads
 *                        after signinRedirectCallback to bounce the user
 *                        back to the originating SPA. Defaults to the
 *                        current pathname+search.
 *   - 2026-05-06 13:00 — `logout()` mirrors that contract: it accepts an
 *                        optional `returnTo` carried through the OIDC
 *                        signout `state` param, so the auth-shell can
 *                        bounce the user back after Zitadel redirects
 *                        to /auth/logout.
 */
import { createContext, useContext, useEffect, useMemo, useState, } from 'react';
import { createUserManager, getAccessToken as _getAccessToken } from './userManager.js';
const AuthContext = createContext(null);
export function AuthProvider({ config, children, }) {
    // Singleton — config is taken from the first render. The provider is
    // intentionally non-rerunnable with a different config; see userManager.ts.
    const um = useMemo(() => createUserManager(config), [config]);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => {
        let cancelled = false;
        async function bootstrap() {
            try {
                const search = window.location.search;
                if (search.includes('code=') && search.includes('state=')) {
                    await um.signinRedirectCallback();
                    // Strip OIDC params from the URL so a manual reload doesn't
                    // re-trigger the callback.
                    const url = new URL(window.location.href);
                    for (const k of ['code', 'state', 'session_state', 'iss']) {
                        url.searchParams.delete(k);
                    }
                    window.history.replaceState({}, document.title, url.toString());
                }
                const u = await um.getUser();
                if (cancelled)
                    return;
                setUser(u && !u.expired ? u : null);
                setLoading(false);
            }
            catch (err) {
                if (cancelled)
                    return;
                setError(err instanceof Error ? err : new Error(String(err)));
                setLoading(false);
            }
        }
        void bootstrap();
        const onLoaded = (u) => setUser(u);
        const onUnloaded = () => setUser(null);
        um.events.addUserLoaded(onLoaded);
        um.events.addUserUnloaded(onUnloaded);
        return () => {
            cancelled = true;
            um.events.removeUserLoaded(onLoaded);
            um.events.removeUserUnloaded(onUnloaded);
        };
    }, [um]);
    const value = useMemo(() => {
        const profile = user?.profile;
        const authUser = profile
            ? {
                sub: typeof profile.sub === 'string' ? profile.sub : '',
                email: typeof profile.email === 'string' ? profile.email : undefined,
                name: typeof profile.name === 'string' ? profile.name : undefined,
                picture: typeof profile.picture === 'string' ? profile.picture : undefined,
                raw: profile,
            }
            : null;
        return {
            isLoading: loading,
            isAuthenticated: user != null,
            user: authUser,
            error,
            login: (returnTo) => um.signinRedirect({
                state: {
                    returnTo: returnTo ?? window.location.pathname + window.location.search,
                },
            }),
            logout: (returnTo) => um.signoutRedirect({
                state: {
                    returnTo: returnTo ?? window.location.pathname + window.location.search,
                },
            }),
            getAccessToken: _getAccessToken,
        };
    }, [user, loading, error, um]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be called inside <AuthProvider>.');
    }
    return ctx;
}
const ZITADEL_PROJECT_ROLES_CLAIM = 'urn:zitadel:iam:org:project:roles';
/**
 * Returns the flat list of Zitadel project-role keys the current user
 * holds, or an empty array when unauthenticated / no role claim is
 * present. Stable across renders for the same underlying claim.
 */
export function useRoles() {
    const { user } = useAuth();
    return useMemo(() => {
        const raw = user?.raw?.[ZITADEL_PROJECT_ROLES_CLAIM];
        if (!raw || typeof raw !== 'object')
            return [];
        return Object.keys(raw);
    }, [user]);
}
/** True iff the current user holds the given project role. */
export function useHasRole(role) {
    return useRoles().includes(role);
}
//# sourceMappingURL=AuthProvider.js.map